/**
 * AIMDv2 – Type Definitions
 *
 * Central type registry for the entire AI Masterclass Design v2 flow.
 * Every step's form data, the unified wizard state, and shared enums live here
 * so that components, hooks, and (future) API layers share a single source of truth.
 *
 * Backend model references:
 *  - OrgDoc (IOrgDoc)  → DocumentItem mapping
 *  - Group.buildState  → AIMDv2BuildState (persisted JSON)
 *  - ProgramGeneration → generationId tracking
 */

import type { AudienceRule } from '../../../utils/memberAttributes';

export type { AudienceRule };

// ---------------------------------------------------------------------------
// Step Identifiers
// ---------------------------------------------------------------------------

export const AIMD_STEPS = [
  'requirements',
  'outcomes',
  'documents',
  'preview',
  'structure',
  'content',
  'members'
] as const;

export type AIMDStepId = (typeof AIMD_STEPS)[number];

export type StepState = 'completed' | 'current' | 'default';

export interface StepMeta {
  id: AIMDStepId;
  label: string;
  showIcon: boolean;
}

// ---------------------------------------------------------------------------
// Step 1 – Requirements
// ---------------------------------------------------------------------------

// -- Member Attributes (backend model) --

/** A single selectable value inside an attribute group (e.g. "HR", "NYC"). */
export interface MemberAttributeValue {
  _id: string;
  value: string;
}

/** An attribute group from the org catalog (e.g. "Department", "Location"). */
export interface MemberAttributeGroup {
  _id: string;
  name: string;
  values: MemberAttributeValue[];
}

/** Response shape from GET /api/groups/{grpId}/memberAttributes. */
export interface MemberAttributesResponse {
  orgMemberAttributes: MemberAttributeGroup[];
  /** Array of value _ids already selected for this program. */
  grpMemberAttributes: string[];
}

/**
 * Payload item for PUT /api/groups/{grpId}/memberAttributes.
 * Each group is sent with ALL its values, each marked selected true/false.
 */
export interface MemberAttributeUpdateItem {
  _id: string;
  name: string;
  values: Array<{
    _id: string;
    value: string;
    selected: boolean;
  }>;
}

// -- Frontend dropdown option (derived from backend data) --

export interface AudienceOption {
  /** The value._id from the backend – this is what gets stored in audience[]. */
  value: string;
  /** Display name (e.g. "HR", "Engineering"). */
  label: string;
  /** Group name (e.g. "Department", "Location") – used for section headers & chip prefix. */
  category?: string;
  /** Group _id from the backend – needed for the PUT payload. */
  categoryId?: string;
}

export interface ToneOption {
  value: string;
  label: string;
  description: string;
}

export interface RequirementsData {
  programName: string;
  programDescription: string;
  /** Array of MemberAttributeValue._id strings. Flat union across every list —
   *  still what the AI generation payload and the legacy memberAttributes save
   *  consume. Derived from `audienceRules`; do not edit independently. */
  audience: string[];
  /** Per-list audience rules (any/all within a list, AND across lists). Drives
   *  enrollment; `audience` is the flattened mirror of this. */
  audienceRules: AudienceRule[];
  tone: string[];
}

// ---------------------------------------------------------------------------
// Step 2 – Outcomes
// ---------------------------------------------------------------------------

export interface OutcomeItem {
  id: string;
  value: string;
}

export interface OutcomesData {
  outcomes: OutcomeItem[];
}

// ---------------------------------------------------------------------------
// Step 3 – Documents
// ---------------------------------------------------------------------------

export type DocumentSourceType = 'file' | 'text' | 'url';

/**
 * Backend document processing statuses.
 * "uploaded" → just received; "processing" → being indexed;
 * "indexed" → ready to use; "failed" → error during processing.
 */
export type DocumentProcessingStatus = 'uploaded' | 'processing' | 'indexed' | 'failed';

/**
 * Frontend representation of an org document.
 * Maps from the backend IOrgDoc model but adds UI-specific fields.
 */
export interface DocumentItem {
  /** Backend _id (MongoDB ObjectId string). */
  id: string;
  type: DocumentSourceType;
  title: string;
  /** Display label: "PDF", "DOCX", "TEXT", "URL", etc. */
  fileTypeLabel: string;
  /** User-provided instructions for AI processing. */
  instructions: string;
  /** Backend processing status — null for locally-added items not yet sent. */
  processingStatus: DocumentProcessingStatus | null;
  /** Download/view URL for files; or the source URL for type=url. */
  documentUrl?: string;
  /** For type=file – the browser File object (not persisted to localStorage). */
  file?: File;
  /** For type=text – the raw text body. */
  textContent?: string;
  /** For type=url – the URL string. */
  url?: string;
  /** Whether this doc was uploaded in the current wizard session. */
  isNew: boolean;
  /** Whether this doc is selected / toggled on for use in the program. */
  selected: boolean;
}

export interface DocumentsData {
  uploadedDocuments: DocumentItem[];
  previousDocuments: DocumentItem[];
}

// ---------------------------------------------------------------------------
// Step 4 – Preview
// ---------------------------------------------------------------------------

export interface PreviewData {
  generatedPreview: string | null;
}

// ---------------------------------------------------------------------------
// Steps 5–6 – Structure & Content (backend model types)
// ---------------------------------------------------------------------------

export interface Material {
  _id: string;
  title: string;
  description: string;
  /** Cover image for the resource card — falls back to the org logo when absent. */
  thumbnail?: string;
}

export interface MCQ {
  question: string;
  options: string[];
  correct_answer: string;
  /** Elliott quiz step — explainer shown to the learner after answering. */
  explanation?: string;
  /** One rationale per option (aligned with `options` by index) — why each is right/wrong. */
  optionReasonings?: string[];
}

// ─── Elliott course redesign — sections attached to each generated lesson ─────

export interface ElliottCaseStudySection {
  label: string; // "Scenario" | "Action" | "Relevance"
  text: string;
}

export interface ElliottCaseStudy {
  id: string;
  title: string;
  sections: ElliottCaseStudySection[];
}

export interface ElliottSimulationRubric {
  brief: string;
  skill: string;
  strongPoints: string[];
  weakPoints: string[];
  maxAttempts: number;
}

export interface ElliottQuizQuestion {
  question: string;
  options: string[];
  /** Encrypted on the way out of the BE (same treatment as mcq.correct_answer). */
  correct_answer: string | null;
  explanation: string;
  /** One rationale per option (aligned with `options` by index). */
  optionReasonings?: string[];
}

export interface ElliottSections {
  takeaway: string | null;
  applicationIntro: string[];
  caseStudies: ElliottCaseStudy[];
  /** Admin toggle for the Case Studies step — absent/undefined means enabled. */
  caseStudiesEnabled?: boolean;
  simulationRubric: ElliottSimulationRubric | null;
  /** Admin toggle for the Live Simulation step — absent/undefined means enabled. */
  simulationEnabled?: boolean;
  /** Admin toggle for the Quiz step — absent/undefined means enabled. */
  quizEnabled?: boolean;
  quiz: ElliottQuizQuestion[];
  generatedAt: number | null;
}

export interface LessonStructure {
  title: string;
  reasoning: string;
  /**
   * A program built in the Capability build flow tags each lesson: the one
   * capability it builds, or the learning outcomes it serves, and whether it
   * ends on a graded Roleplay (see ../lessonTags.ts). Absent elsewhere.
   */
  capabilityId?: string | null;
  outcomes?: string[];
  roleplay?: boolean;
}

export interface ModuleStructure {
  title: string;
  reasoning: string;
  learningOutcomesServed?: string[];
  ragDocumentTitles?: string[];
  lessons: LessonStructure[];
}

export interface ProgramStructure {
  modules: ModuleStructure[];
}

export type LessonStatus = 'pending' | 'generating' | 'completed' | 'failed';
export type ModuleStatus = 'pending' | 'generating' | 'completed' | 'failed';
export type GenerationOverallStatus = 'pending' | 'generating' | 'completed' | 'failed';

export interface GeneratedLesson extends LessonStructure {
  status: LessonStatus;
  content: string;
  materials: Material[];
  mcq: MCQ | null;
  mcqs?: MCQ[];
  usedRag: boolean;
  generatedAt: number | null;
  learningOutcomesServed?: string[];
  ragDocumentTitles?: string[];
  /** Elliott sections (case studies · simulation rubric · quiz), generated per-lesson on the BE. */
  elliott?: ElliottSections | null;
}

export interface GeneratedModule extends Omit<ModuleStructure, 'lessons'> {
  status: ModuleStatus;
  completedLessonsCount: number;
  totalLessonsCount: number;
  lessons: GeneratedLesson[];
}

export interface GenerationProgress {
  completedLessons: number;
  totalLessons: number;
  percentage: number;
}

export interface GenerationStatus {
  generationId: string;
  status: GenerationOverallStatus;
  progress: GenerationProgress;
  programPreview: string | null;
  modules: GeneratedModule[];
  createdAt: number | null;
  updatedAt: number | null;
  startedAt: number | null;
  completedAt: number | null;
  error: string | null;
}

export interface StructureData {
  generatedStructure: ProgramStructure | null;
}

export interface ContentData {
  generatedContent: GenerationStatus | null;
}

// ---------------------------------------------------------------------------
// Step 7 – Members
// ---------------------------------------------------------------------------

export interface MembersData {
  memberIds: string[];
}

// ---------------------------------------------------------------------------
// API Payload Types
// ---------------------------------------------------------------------------

export interface AudiencePayloadItem {
  attribute: string;
  value: string;
}

export interface GeneratePreviewPayload {
  generalPurpose: string;
  learningObjectives: string[];
  programName?: string;
}

export interface ImprovePreviewPayload {
  generalPurpose: string;
  learningObjectives: string[];
  currentPreview: string;
  feedback: string;
  programName?: string;
}

export interface GenerateStructurePayload {
  generalPurpose: string;
  learningObjectives: string[];
  groupId: string;
  orgDocsIds?: string[];
  programPreview?: string;
}

export interface GenerateContentPayload {
  generalPurpose: string;
  learningObjectives: string[];
  modules: Array<{ title: string; lessons: Array<{ title: string }> }>;
  orgDocsIds: string[];
  audience?: AudiencePayloadItem[];
  tone?: string[];
  programPreview?: string;
}

export interface ImproveModulePayload {
  generalPurpose: string;
  learningObjectives: string[];
  moduleIndex: number;
  feedback: string;
  programPreview?: string;
}

export interface ImproveProgramPayload {
  generalPurpose: string;
  learningObjectives: string[];
  feedback: string;
  programPreview?: string;
}

export interface RegenerateLessonPayload {
  moduleIndex: number;
  lessonIndex: number;
  feedback: string;
}

export interface RegenerateModulePayload {
  moduleIndex: number;
  feedback: string;
}

export interface RegenerateProgramPayload {
  feedback: string;
}

export type GenerationPhase = 'idle' | 'preview' | 'structure' | 'content';

// ---------------------------------------------------------------------------
// Unified Wizard State (Frontend)
// ---------------------------------------------------------------------------

export interface AIMDv2WizardState {
  currentStep: number;
  highestCompletedStep: number;
  requirements: RequirementsData;
  outcomes: OutcomesData;
  documents: DocumentsData;
  preview: PreviewData;
  structure: StructureData;
  content: ContentData;
  members: MembersData;
  lastSavedAt: string | null;
  /** Backend program-generation record ID. */
  generationId: string | null;
  /** The groupId this wizard is operating on. */
  groupId: string | null;
}

// ---------------------------------------------------------------------------
// BuildState – the JSON shape sent to POST /api/groups/{grpId}/buildState
// ---------------------------------------------------------------------------

/**
 * Serialisable subset of AIMDv2WizardState that gets persisted to the backend
 * via the buildState endpoint. File objects and derived UI state are excluded.
 */
export interface AIMDv2BuildState {
  currentStep: number;
  highestCompletedStep: number;
  requirements: RequirementsData;
  outcomes: {
    learningObjectives: string[];
  };
  documents: {
    /** IDs of org docs selected for use in this program. */
    selectedDocIds: string[];
  };
  /** Generated program preview text. Persisted so a cross-device resume on the
   *  Preview step reopens with the existing preview instead of regenerating. */
  preview?: string | null;
  ai: {
    structure: unknown | null;
    content: unknown | null;
  };
  generationId: string | null;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export interface StepValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}
